/**
 * `room.isCheckpoint` desugared — D142.
 *
 * @remarks
 * The general primitive is the `checkpoint` EFFECT; a room that "is a
 * checkpoint" is sugar for a repeatable trigger that fires the effect on
 * every executed turn spent in the room, so the checkpoint is the room as
 * the player last left it. The sugar is rewritten into that trigger here,
 * at load, BEFORE semantic validation — so the synthesized trigger is
 * validated, indexed, and evaluated exactly like an authored one, and the
 * loaded bundle shows the primitive rather than the sugar (§2.1: the general
 * shape with sugar desugaring into it, never two implementations).
 *
 * The trigger's id is `trigger_checkpoint_<roomId>`; an author who declares
 * that id by hand collides in the duplicate-id check, which is the honest
 * outcome. The room keeps its `isCheckpoint` flag (the document is not
 * mutated; only the loaded pack gains the trigger), so the audit, the
 * studio, and a future migration all still see what the author wrote.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, TriggerDefinition, TriggerId } from '@orpheus/content-schema';

/**
 * The trigger a checkpoint room desugars into.
 *
 * @param room - A room declaring `isCheckpoint`
 * @returns The repeatable trigger
 */
const checkpointTriggerFor = (room: ContentPackDefinition['rooms'][number]): TriggerDefinition => ({
	conditions: [{ roomId: room.roomId, type: 'room_equals' }],
	effects: [{ type: 'checkpoint' }],
	on: { type: 'command_executed' },
	repeatable: true,
	triggerId: `trigger_checkpoint_${room.roomId}` as TriggerId,
});

/**
 * The pack with every `isCheckpoint` room's trigger appended, in room order,
 * after the authored triggers.
 *
 * @param contentPack - The parsed pack
 * @returns The same pack, or one with the synthesized triggers appended
 */
export const desugarCheckpointRooms = (contentPack: ContentPackDefinition): ContentPackDefinition => {
	const synthesized = contentPack.rooms.filter((room) => room.isCheckpoint === true).map(checkpointTriggerFor);

	return synthesized.length === 0 ? contentPack : { ...contentPack, triggers: [...contentPack.triggers, ...synthesized]};
};
